import { Fragment } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Users } from "@phosphor-icons/react";

import { SectionHeader } from "@/components/brand-intelligence/layout/heading";
import { AgeDistribution } from "@/components/brand-intelligence/charts/age-distribution";
import { DecisionJourney } from "@/components/brand-intelligence/charts/decision-journey";
import { EducationLevel } from "@/components/brand-intelligence/charts/education-level";
import { GenderIncome } from "@/components/brand-intelligence/charts/gender-income";
import { BrandAffinities } from "@/components/brand-intelligence/metrics/brand-affinities";
import { GeographicDistribution } from "@/components/brand-intelligence/metrics/geographic-distribution";
import { MediaDeviceUsage } from "@/components/brand-intelligence/metrics/media-device-usage";
import { ShoppingBehavior } from "@/components/brand-intelligence/metrics/shopping-behavior";
import { KeyCharacteristicsInterests } from "@/components/brand-intelligence/charts/key-characteristics-interests";

export const Route = createFileRoute("/dashboard/research/_layout/$id/audience")({
  component: RouteComponent,
});

function RouteComponent() {
  return (
    <Fragment>
      <SectionHeader title="Audience Intelligence" icon={<Users className="text-blue-500 size-6" />} iconClassName="bg-blue-500/10" />
      <div className="space-y-6">
        <Demographics />
        <Psychographics />
        <BehavioralInsights />
      </div>
    </Fragment>
  );
}

function PsychographicsSectionHeader({ title, description }: { title: string; description: string }) {
  return (
    <div className="flex items-start gap-4">
      <div>
        <h4 className="text-base font-semibold text-foreground">{title}</h4>
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>
    </div>
  );
}

function Demographics() {
  return (
    <Fragment>
      <PsychographicsSectionHeader title="Demographics" description="Age, gender, income, education, and geographic distribution" />
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
        <AgeDistribution />
        <GenderIncome />
        <EducationLevel />
        <GeographicDistribution />
      </div>
    </Fragment>
  );
}

function Psychographics() {
  return (
    <Fragment>
      <PsychographicsSectionHeader title="Psychographics" description="Key characteristics and interests" />
      <div className="grid gap-6 grid-cols-1 lg:grid-cols-2">
        <KeyCharacteristicsInterests />
        <BrandAffinities />
      </div>
    </Fragment>
  );
}

function BehavioralInsights() {
  return (
    <Fragment>
      <PsychographicsSectionHeader title="Behavioral Insights" description="Shopping behavior, decision journey, and media & device usage" />
      <div className="grid gap-6 grid-cols-1 md:grid-cols-2 xl:grid-cols-3">
        <ShoppingBehavior />
        <DecisionJourney />
        <MediaDeviceUsage />
      </div>
    </Fragment>
  );
}
